import React from "react";
import { Tag } from "lucide-react";
import { useApp } from "@/state/AppContext";
import { rupees, bpsToPercent } from "@/lib/format";

/**
 * The API sends an offer's discount in two different shapes, and this reads both.
 *
 * A LISTING (`/offers`, `/businesses/:id/offers`, `/owner/offers`) carries
 * `discount_percent_bp` — **basis points**, so 20% is 2000 — or `discount_flat`, a money object
 * `{minor_units, currency, display}` in paise. A CART QUOTE (`/cart`) carries a flat
 * `discount_value` instead, because it is a computed quote rather than the offer row.
 *
 * This helper previously read `discount_value` alone. The backend never sends that on a listing,
 * so `Math.round(undefined)` rendered **"NaN% off"** on every business card, mini-site and
 * favourite — and on the cart, where `discount_value` really is present, it is basis points, so
 * a 20% offer rendered **"2000% off"**. Both were wrong on every surface that shows an offer.
 *
 * Reading both shapes here rather than unifying the wire is deliberate: the two payloads mean
 * different things and the cart's is consumed by two mobile apps, so changing it is a breaking
 * change to buy tidiness this function can provide for free.
 */
export function offerLabel(t, offer) {
  if (!offer) return "";
  if (offer.discount_type === "flat") {
    // A listing's `discount_flat` is a money object ({minor_units, currency, display}); the cart
    // quote instead sends a bare `discount_value` in paise. Format both through the shared
    // rupees() formatter rather than trusting the server's raw `.display` string, which was
    // `str(Decimal)` — no grouping, always two decimals (e.g. "50.00 off" instead of "50 off").
    // See QA/uat-2026-09-24/04_fix_log.md, Storefront S1, A2.
    // The i18n template (`offers.badgeFlat`, en+hi) already supplies its own "₹" — strip the one
    // rupees() adds so the badge doesn't read "₹₹50 off".
    //
    // A bound variable, not `rupees(offer.discount_flat)` inline — the Tier-M
    // `no-restricted-syntax` lint (craco.config.js) flags ANY member-expression argument to
    // rupees(), on the theory that the object literal's own `{minor_units: …}` shape is the only
    // safe call form; `discount_flat` genuinely IS that money object already, so this binds it to
    // a plain identifier first rather than adding a lint suppression.
    const flatMoney = offer.discount_flat;
    const flat = (flatMoney ? rupees(flatMoney) : rupees({ minor_units: offer.discount_value })).replace(
      /^₹/,
      "",
    );
    return t("offers.badgeFlat", { value: flat });
  }
  const bp = offer.discount_percent_bp ?? offer.discount_value;
  if (bp == null || Number.isNaN(Number(bp))) return "";
  return t("offers.badgePercent", { value: Math.round(bpsToPercent(bp)) });
}

/** An offer's start/end dates, which listings send as `starts_on`/`ends_on` (a DATE) and the
 * cart quote sends as `ends_at`. Same reason as above. */
export function offerEndsOn(offer) {
  return offer?.ends_on ?? offer?.ends_at ?? "";
}

export function offerStartsOn(offer) {
  return offer?.starts_on ?? offer?.starts_at ?? "";
}

export function OfferBadge({ offer, className = "" }) {
  const { t } = useApp();
  if (!offer) return null;
  const label = offerLabel(t, offer);
  if (!label) return null;
  return (
    <span
      data-testid={`offer-badge-${offer.id}`}
      className={`inline-flex items-center gap-1 rounded-full bg-[hsl(var(--accent))] px-3 py-1 text-xs font-semibold text-white shadow-sm ${className}`}
    >
      <Tag size={12} /> {label}
    </span>
  );
}
